/**
 * AdvisorReconfirmBanner — §5-Re-Confirm-Hinweis im Advisor-Kontext (B4,
 * architecture-stb-onboarding-consent.md §6.4).
 *
 * Zeigt im Advisor-Kontext (`activeRole === 'advisor'`) einen Hinweis + Aktion,
 * wenn die eigene §5-Onboarding-Version veraltet ist (`onboardingVersion !==`
 * {@link KANZLEI_ONBOARDING_VERSION}). Die Re-Bestätigung läuft über denselben
 * {@link KanzleiOnboardingDialog}.
 *
 * Inert & fail-safe: rendert `null`, solange (a) kein Advisor-Kontext aktiv ist,
 * (b) die §5-Datei nicht freigegeben (`ready`) ist, oder (c) die eigene
 * Bestätigung bereits aktuell ist. Der Server (E3/Preconditions) bleibt die harte
 * Durchsetzung — dieser Banner ist die FE-Aufforderung.
 *
 * @module components/tenant/AdvisorReconfirmBanner
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import {
  KANZLEI_ONBOARDING_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument } from './useLegalDocument';
import { readOwnOnboardingStatus } from '../../utils/advisorOnboarding';
import KanzleiOnboardingDialog from './KanzleiOnboardingDialog';

/**
 * Re-Confirm-Banner. Nur sichtbar bei aktivem Advisor-Kontext, freigegebener
 * §5-Datei und veralteter/fehlender eigener Bestätigung.
 */
const AdvisorReconfirmBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, activeRole } = useAuth();
  const onboardingDoc = useLegalDocument(KANZLEI_ONBOARDING_DOC_PATH, LEGAL_DRAFT_MARKER);

  const [needsReconfirm, setNeedsReconfirm] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const isAdvisorContext = activeRole === 'advisor';
  const docReady = onboardingDoc.kind === 'ready';

  useEffect(() => {
    // Nur prüfen, wenn Advisor-Kontext UND §5-Datei freigegeben (sonst inert).
    if (!isAdvisorContext || !docReady || !currentUser?.uid) {
      setNeedsReconfirm(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const status = await readOwnOnboardingStatus(currentUser.uid);
      if (!cancelled) setNeedsReconfirm(!status.confirmed);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdvisorContext, docReady, currentUser?.uid]);

  if (!isAdvisorContext || !docReady || !needsReconfirm) {
    return null;
  }

  return (
    <div
      className="mb-4 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-4"
      role="status"
      data-testid="advisor-reconfirm-banner"
    >
      <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
      <div className="flex-1">
        <p className="text-sm font-medium text-[var(--tone-warning-fg)]">
          {t('kanzleiOnboarding.reconfirmTitle')}
        </p>
        <p className="mt-1 text-sm text-[var(--tone-warning-fg)]">
          {t('kanzleiOnboarding.reconfirmBanner')}
        </p>
        <button
          type="button"
          onClick={() => setShowDialog(true)}
          className="mt-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
          data-testid="advisor-reconfirm-open"
        >
          {t('kanzleiOnboarding.reconfirmButton')}
        </button>
      </div>

      {showDialog && (
        <KanzleiOnboardingDialog
          onConfirmed={() => {
            setShowDialog(false);
            setNeedsReconfirm(false);
          }}
          onCancel={() => setShowDialog(false)}
        />
      )}
    </div>
  );
};

export default AdvisorReconfirmBanner;
